import { withRef } from "@udecode/cn";
import { TriggerComboboxPluginOptions, withTriggerCombobox } from "@udecode/plate-combobox";
import {
    PluginConfig,
    TElement,
    createSlatePlugin,
    getBlockAbove,
    getEditorPlugin,
    insertNodes,
    insertText,
    isEndPoint,
    moveSelection,
} from "@udecode/plate-common";
import { BaseMentionInputPlugin } from "@udecode/plate-mention";
import { PlateEditor, PlateElement, PlateElementProps, useElement } from "@udecode/plate/react";
import { useState } from "react";
import { useFocused, useSelected } from "slate-react";
import { useQuery } from "urql";

import {
    InlineCombobox,
    InlineComboboxContent,
    InlineComboboxEmpty,
    InlineComboboxGroup,
    InlineComboboxInput,
    InlineComboboxItem,
} from "@/components/plate-ui/inline-combobox";
import { graphql } from "@/gen";
import { cn } from "@/utils";
import { useLanguageCodeWithDefault } from "@/utils/language";

type TExplanation = {
    id: string;
    keyword: string;
};

export interface TExplanationElement extends TElement {
    id: string;
}

export const ExplanationInputPlugin = createSlatePlugin({
    key: "explanation_input",
    node: { isElement: true, isInline: true, isVoid: true },
});

export type ExplanationConfig = PluginConfig<
    "translated_explanation",
    {
        insertSpaceAfterMention?: boolean;
    } & TriggerComboboxPluginOptions,
    object,
    {
        insert: {
            explanation: (options: { search: string; id: string; text: string }) => void;
        };
    }
>;

/** Enables support for autocompleting @mentions. */
export const ExplanationPlugin = createSlatePlugin({
    key: "translated_explanation",
    extendEditor: withTriggerCombobox,
    node: {
        isElement: true,
        isInline: true,
        isMarkableVoid: true,
    },
    options: {
        createComboboxInput: trigger => ({
            children: [{ text: "" }],
            trigger,
            type: ExplanationInputPlugin.key,
        }),
        trigger: "#",
        triggerPreviousCharPattern: /^\s?$/,
    },
    plugins: [BaseMentionInputPlugin],
}).extendEditorTransforms<ExplanationConfig["transforms"]>(({ editor, type }) => ({
    insert: {
        explanation: ({ id, text }) => {
            insertNodes<TExplanationElement>(editor, {
                type,
                id,
                children: [{ text }],
            });
        },
    },
}));

const onSelectItem = (editor: PlateEditor, item: TExplanation, search = "") => {
    const { getOptions, tf } = getEditorPlugin<ExplanationConfig>(editor, {
        key: ExplanationPlugin.key,
    });
    const { insertSpaceAfterMention } = getOptions();

    tf.insert.explanation({ id: item.id, text: item.keyword, search });

    // move the selection after the element
    moveSelection(editor, { unit: "offset" });

    const pathAbove = getBlockAbove(editor)?.[1];

    const isBlockEnd =
        editor.selection && pathAbove && isEndPoint(editor, editor.selection.anchor, pathAbove);

    if (isBlockEnd && insertSpaceAfterMention) {
        insertText(editor, " ");
    }
};

const searchExplanationsQuery = graphql(`
    query SearchTranslatedExplanations($query: String!, $languageCode: String!) {
        searchTranslatedExplanations(input: { query: $query, languageCode: $languageCode }) {
            items {
                id
                keyword
            }
        }
    }
`);

export const ExplanationInputElement = withRef<typeof PlateElement>(
    ({ className, ...props }, ref) => {
        const { children, editor, element } = props;
        const [search, setSearch] = useState("");

        const languageCode = useLanguageCodeWithDefault();
        const [explanations] = useQuery({
            query: searchExplanationsQuery,
            variables: {
                query: search,
                languageCode,
            },
        });

        return (
            <PlateElement ref={ref} as="span" data-slate-value={element.value} {...props}>
                <InlineCombobox
                    value={search}
                    element={element}
                    setValue={setSearch}
                    showTrigger={false}
                    trigger="#"
                >
                    <span
                        className={cn(
                            "ring-ring inline-block rounded-md align-baseline focus-within:ring-2",
                            className
                        )}
                    >
                        <InlineComboboxInput />
                    </span>

                    <InlineComboboxContent className="my-1.5">
                        {explanations.fetching ? (
                            <InlineComboboxEmpty>Loading explanations...</InlineComboboxEmpty>
                        ) : (
                            <InlineComboboxEmpty>No results</InlineComboboxEmpty>
                        )}

                        <InlineComboboxGroup>
                            {explanations.data?.searchTranslatedExplanations.items.map(item => (
                                <InlineComboboxItem
                                    key={item.id}
                                    value={item.keyword}
                                    onClick={() => onSelectItem(editor, item, search)}
                                >
                                    {item.keyword}
                                </InlineComboboxItem>
                            ))}
                        </InlineComboboxGroup>
                    </InlineComboboxContent>
                </InlineCombobox>

                {children}
            </PlateElement>
        );
    }
);

export const ExplanationElement = ({ className, ...props }: PlateElementProps) => {
    const element = useElement<TExplanationElement>();
    const selected = useSelected();
    const focused = useFocused();

    return (
        <PlateElement
            as="strong"
            className={cn(
                "border-brand-white text-brand-white inline-block cursor-pointer rounded-md border-b border-dashed align-baseline",
                selected && focused && "ring-ring ring-2",
                className
            )}
            data-slate-value={element.id}
            {...props}
        />
    );
};
